import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Box } from '@mui/material';

interface ScrollIndicatorBoxProps {
  theme: any;
  maxHeight: number | string;
  sx?: any;
  children: React.ReactNode;
}

export const ScrollIndicatorBox: React.FC<ScrollIndicatorBoxProps> = ({ theme, maxHeight, sx, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = useState<{ visible: boolean; top: number; height: number }>({
    visible: false,
    top: 0,
    height: 0,
  });

  const recompute = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const { scrollHeight, clientHeight, scrollTop } = el;
    if (scrollHeight <= clientHeight + 1) {
      setThumb((prev) => (prev.visible ? { visible: false, top: 0, height: 0 } : prev));
      return;
    }
    const trackHeight = clientHeight;
    const thumbHeight = Math.max(24, (clientHeight / scrollHeight) * trackHeight);
    const maxTop = trackHeight - thumbHeight;
    const progress = scrollTop / (scrollHeight - clientHeight);
    setThumb({ visible: true, top: maxTop * progress, height: thumbHeight });
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    recompute();
    const observer = new ResizeObserver(recompute);
    observer.observe(el);
    Array.from(el.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [recompute, children]);

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        ref={ref}
        onScroll={recompute}
        sx={{
          maxHeight,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { width: 0, height: 0 },
          pr: thumb.visible ? 1 : 0,
          ...sx,
        }}
      >
        {children}
      </Box>
      {thumb.visible && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            right: 2,
            width: 6,
            borderRadius: 3,
            backgroundColor: `${theme.colors.border}55`,
            pointerEvents: 'none',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              width: 6,
              borderRadius: 3,
              backgroundColor: theme.colors.text.secondary,
              top: `${thumb.top}px`,
              height: `${thumb.height}px`,
            }}
          />
        </Box>
      )}
    </Box>
  );
};
